import React from "react";
import { connect } from "react-redux";
import { showSnack } from "react-redux-snackbar";
import { Button, Form, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { ThunkDispatch } from "redux-thunk";
import { Icon } from "@cadmakers-ui/icons";
import { Comment as UIComment, Header as UIHeader } from "semantic-ui-react";
import { Comment, CreateComment, EditComment } from "../../../Models/Comment";
import { ResponseResult } from "../../../../Common/Models/ResponseResult";
import ResponseHandler from "../../../../App/ResponseHandler";
import { ApiRequest, ErrorDisplay, IAppState, LoadingDisplay } from "../../../../Common";
import FileUploadButton from "../../../../Common/Components/FileUploadButton/FileUploadButton";
import { endpoints } from "../../..";
import ElementSpacer from "../../../../Layout/Components/ElementSpacer";
import { fileService, imageUploadService } from "../../../Services";
import { Image } from "../../../Models/Image";
import { DeleteImage } from "../../../Models/DeleteImage";
import "../../../Styles/Comment.scss";
import "@webscopeio/react-textarea-autocomplete/style.css";
import "../../../Styles/CommentBox.scss";
import { CommentUpdateResultDto } from "../../../Models/CommentUpdateResultDto";
import { CommentInput } from "./CommentInput";
import CommentItem from "./CommentItem";
import iconStyles from "../../../../Common/Styles/Icons.module.scss";

type Props = {
  code: string;
  shouldAutocompleteForOtherCompanyUsers: boolean;
};

type DispatchProps = {
  showSnack: (uniqueId: any, args: any) => void;
};

function mapDispatchToProps(dispatch: ThunkDispatch<IAppState, {}, any>): DispatchProps {
  return {
    showSnack: (uniqueId: any, args: any) => dispatch(showSnack(uniqueId, args))
  };
}

type State = {
  threadReply: string;
  comments: Comment[];
  activeReplyId: number | null;
  activeEditId: number | null;
  commentsHasError: boolean;
  errorCode?: number;
  errorResult?: string;
  errorDescription?: string;
  contentIsLoaded: boolean;
  threadReplyImages: Image[];
  isDeleteModalOpen: boolean;
  deleteModalCommentId?: number;
};

class CommentBox extends React.Component<Props & DispatchProps, State> {
  private uploadPendingCount: number;

  constructor(props: Props & DispatchProps) {
    super(props);

    this.state = {
      threadReply: "",
      comments: [],
      activeReplyId: null,
      activeEditId: null,
      commentsHasError: false,
      contentIsLoaded: false,
      threadReplyImages: [],
      isDeleteModalOpen: false
    };

    this.uploadPendingCount = 0;

    this.toggleDeleteModalFromModal = this.toggleDeleteModalFromModal.bind(this);
    this.toggleDeleteModal = this.toggleDeleteModal.bind(this);
  }

  componentDidMount = () => {
    this.getComments();
  };

  setActiveReplyId = (id: number | null) => {
    this.setState({
      activeReplyId: id,
      activeEditId: null
    });
  };

  setActiveEditId = (id: number | null) => {
    this.setState({
      activeEditId: id,
      activeReplyId: null
    });
  };

  threadReplyChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    this.setState({
      threadReply: event.target.value
    });
  };

  createThreadReply = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (this.uploadPendingCount > 0) {
      this.showErrorSnackbar("Failed to create reply", "Please wait until all images are uploaded");
    }

    const reply: CreateComment = {
      comment: this.state.threadReply,
      parentCommentId: null,
      code: this.props.code,
      imageRefs: this.state.threadReplyImages
    };

    this.postComment(reply, () => {
      this.getComments();
      this.setState({
        threadReply: "",
        threadReplyImages: []
      });
    });
  };

  createCommentReply = (comment: CreateComment, success: () => void) => {
    this.postComment(comment, () => {
      this.getComments();
      this.setActiveReplyId(null);
      success();
    });
  };

  editComment = (
    commentId: number,
    comment: EditComment,
    newImages: Image[],
    isDeleted: boolean,
    success: () => void
  ) => {
    this.updateComment(commentId, comment, (result: CommentUpdateResultDto) => {
      const commentsCopy = [...this.state.comments];
      const updatedComments = this.updateEditedComment(
        commentId,
        comment,
        newImages,
        commentsCopy,
        isDeleted,
        result
      );

      this.setState(
        {
          comments: []
        },
        () =>
          this.setState(
            {
              comments: updatedComments
            },
            () => success()
          )
      );

      this.setActiveEditId(null);
    });
  };

  updateEditedComment = (
    commentId: number,
    newComment: EditComment,
    newImages: Image[],
    comments: Comment[],
    isDeleted: boolean,
    updateResult: CommentUpdateResultDto
  ) => {
    this.findComment(commentId, comments, (comment: Comment) => {
      Object.assign(comment, comment, {
        comment: isDeleted ? "Deleted" : updateResult.newText,
        originalCommentText: updateResult.newOriginalText,
        pings: updateResult.pings,
        images: newImages,
        isDeleted
      });
    });

    return comments;
  };

  getComments = () => {
    return ApiRequest.fromPath(endpoints.comments.get(this.props.code))
      .withAuthHeader()
      .get()
      .then((response: Response) => {
        ResponseHandler.handle(
          response,
          (result: ResponseResult) => {
            this.setState({
              comments: result.payload,
              contentIsLoaded: true
            });
          },
          (result: ResponseResult) => {
            this.setState({
              commentsHasError: true,
              errorCode: response.status,
              errorDescription: result.error.message
            });
          },
          () => {
            this.setState({
              commentsHasError: true,
              errorCode: response.status,
              errorDescription: "An unknown error has occured"
            });
          }
        );
      });
  };

  postComment = (comment: CreateComment, action: () => void) => {
    return ApiRequest.fromPath(endpoints.comments.create())
      .withJson(comment)
      .withAuthHeader()
      .post()
      .then((response: Response) => {
        ResponseHandler.handle(
          response,
          () => {
            action();
          },
          (result: ResponseResult) => {
            this.showErrorSnackbar(
              "Failed to create comment",
              result.error.message,
              response.status
            );
          },
          () => {
            this.showErrorSnackbar(
              "Failed to create comment",
              "An unknown error has occured",
              response.status
            );
          }
        );
      });
  };

  updateComment = (
    id: number,
    comment: EditComment,
    action: (result: CommentUpdateResultDto) => any
  ) => {
    return ApiRequest.fromPath(endpoints.comments.edit(id))
      .withAuthHeader()
      .withJson(JSON.stringify(comment))
      .put()
      .then((response: Response) => {
        ResponseHandler.handle(
          response,
          (result: ResponseResult) => {
            const payload = result.payload as CommentUpdateResultDto;

            action(payload);
          },
          (result: ResponseResult) => {
            this.showErrorSnackbar("Failed to edit comment", result.error.message, response.status);
          },
          () => {
            this.showErrorSnackbar(
              "Failed to edit comment",
              "An unknown error has occured",
              response.status
            );
          }
        );
      });
  };

  deleteComment = (id: number) => {
    ApiRequest.fromPath(endpoints.comments.delete(id))
      .withAuthHeader()
      .delete()
      .then((response: Response) => {
        ResponseHandler.handle(
          response,
          () => {
            // update the comment in the list to be deleted
            const commentsCopy = [...this.state.comments];
            const updatedComments = this.updateEditedComment(
              id,
              { comment: "", imageRefs: [] },
              [],
              commentsCopy,
              true,
              { newText: "", newOriginalText: "", pings: [] }
            );

            this.setState(
              {
                comments: [],
                isDeleteModalOpen: false,
                deleteModalCommentId: undefined
              },
              () =>
                this.setState({
                  comments: updatedComments
                })
            );

            this.props.showSnack("COMMENT_BOX", {
              label: "Successfully deleted comment",
              timeout: 5000
            });
          },
          (result: ResponseResult) => {
            this.showErrorSnackbar(
              "Failed to delete comment",
              result.error.message,
              response.status
            );
          },
          () => {
            this.showErrorSnackbar(
              "Failed to delete comment",
              "An unknown error has occured",
              response.status
            );
          }
        );
      });
  };

  handleLike = (commentId: number) => {
    ApiRequest.fromPath(endpoints.comments.vote(commentId))
      .withAuthHeader()
      .post()
      .then((response: Response) => {
        ResponseHandler.handle(
          response,
          null,
          (result: ResponseResult) => {
            this.showErrorSnackbar("Failed to vote", result.error.message, response.status);
          },
          () => {
            this.showErrorSnackbar(
              "Failed to vote",
              "An unknown error has occured",
              response.status
            );
          }
        );
      });
  };

  findComment = (commentId: number, comments: Comment[], action: (comment: Comment) => any) => {
    for (let i = 0; i < comments.length; i++) {
      if (comments[i].id === commentId) {
        action(comments[i]);

        break;
      } else {
        this.findComment(commentId, comments[i].childComments, action);
      }
    }
  };

  clearThreadReply = () => {
    this.setState({
      threadReply: ""
    });
  };

  onImagesAdded = (images: Image[]) => {
    this.uploadPendingCount += images.length;

    images.forEach(async (image: Image) => {
      await imageUploadService.uploadImage(
        endpoints.comments.images.uploadTemp(this.props.code),
        image,
        newImage => {
          this.setState(prevState => ({
            threadReplyImages: prevState.threadReplyImages.concat(newImage)
          }));
        },
        hash => {
          return this.state.threadReplyImages.find(x => x.fullSizeHash === hash);
        },
        () => {
          this.showErrorSnackbar("Failed to upload image", "This image has already been uploaded");
        },
        (response, result) => {
          this.showErrorSnackbar("Failed to upload image", result.error.message, response.status);
        },
        response => {
          this.showErrorSnackbar(
            "Failed to upload image",
            "An unknown error has occured",
            response.status
          );
        }
      );

      this.uploadPendingCount--;
    });
  };

  onImageDelete = (image: Image) => {
    const deleteModel: DeleteImage = {
      fullSizeObjectPath: image.fullSizeObjectPath,
      thumbnailObjectPath: image.thumbnailObjectPath
    };

    imageUploadService.deleteImage(
      endpoints.comments.images.deleteTemp(),
      deleteModel,
      () => {
        this.setState(prevState => ({
          threadReplyImages: prevState.threadReplyImages.filter(
            i => i.fullSizeHash !== image.fullSizeHash
          )
        }));
      },
      (response, result) => {
        this.showErrorSnackbar("Failed to delete image", result.error.message, response.status);
      },
      response => {
        this.showErrorSnackbar(
          "Failed to delete image",
          "An unknown error has occured",
          response.status
        );
      }
    );
  };

  showErrorSnackbar = (reason: string, errorDescription: string, errorCode?: number) => {
    let label = `${reason}: ${errorDescription}`;

    if (errorCode) {
      label += `(Error Code ${errorCode})`;
    }

    this.props.showSnack("COMMENT_BOX", {
      label,
      timeout: 5000
    });
  };

  onUploadClick = (files: File[]) => {
    this.onImagesAdded(fileService.filestoImages(files));
  };

  toggleDeleteModalFromModal = () => {
    this.toggleDeleteModal();
  };

  toggleDeleteModal = (deletingCommentId?: number) => {
    this.setState((prevState: State) => ({
      isDeleteModalOpen: !prevState.isDeleteModalOpen,
      deleteModalCommentId: deletingCommentId
    }));
  };

  render() {
    if (this.state.commentsHasError) {
      return (
        <ErrorDisplay
          errorCode={this.state.errorCode}
          errorDescription={this.state.errorDescription}
          errorResult="Could not load comments from server"
        />
      );
    }
    return (
      <div>
        <UIComment.Group threaded>
          <UIHeader as="h4" dividing>
            Comments
          </UIHeader>

          <Form className="ui reply form" onSubmit={this.createThreadReply} acceptCharset="UTF-8">
            <CommentInput
              value={this.state.threadReply}
              onValueChange={this.threadReplyChangeHandler}
              onValueClear={this.clearThreadReply}
              images={this.state.threadReplyImages}
              onImagesAdded={this.onImagesAdded}
              onImageDelete={this.onImageDelete}
              shouldAutocompleteForOtherCompanyUsers={
                this.props.shouldAutocompleteForOtherCompanyUsers
              }
            />
            <ElementSpacer>
              <Button color="primary" className="comment-button">
                <span>
                  <Icon
                    appName="GeneralLayout"
                    assetName="GENERAL_L_CreateEdit"
                    className={iconStyles.smallIcon}
                  />
                </span>
                Add Comment
              </Button>
              <FileUploadButton className="comment-button" onClick={this.onUploadClick}>
                Upload Images
              </FileUploadButton>
            </ElementSpacer>
          </Form>

          {this.state.contentIsLoaded ? (
            this.state.comments.map((comment: Comment) => (
              <CommentItem
                key={comment.id}
                depth={1}
                comment={comment}
                code={this.props.code}
                activeReplyId={this.state.activeReplyId}
                activeEditId={this.state.activeEditId}
                setActiveReplyId={this.setActiveReplyId}
                setActiveEditId={this.setActiveEditId}
                createReplyHander={this.createCommentReply}
                editCommentHandler={this.editComment}
                deleteCommentHandler={this.toggleDeleteModal}
                childComments={comment.childComments}
                onLike={this.handleLike}
                shouldAutocompleteForOtherCompanyUsers={
                  this.props.shouldAutocompleteForOtherCompanyUsers
                }
              />
            ))
          ) : (
            <LoadingDisplay />
          )}
        </UIComment.Group>
        <Modal isOpen={this.state.isDeleteModalOpen} toggle={this.toggleDeleteModalFromModal} class>
          <ModalHeader toggle={this.toggleDeleteModalFromModal}>
            Are you sure you want to delete this comment?
          </ModalHeader>
          <ModalBody>This cannot be undone</ModalBody>
          <ModalFooter>
            <Button
              color="danger"
              onClick={() => {
                this.deleteComment(
                  this.state.deleteModalCommentId ? this.state.deleteModalCommentId : 0
                );
              }}
            >
              Delete
            </Button>
            <Button color="secondary" onClick={this.toggleDeleteModalFromModal}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
        <hr />
      </div>
    );
  }
}

export default connect(null, mapDispatchToProps)(CommentBox);
